import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Edit,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { getTodayInUserTimezone, formatDateForDisplay, utcDateToUserTimezoneDateString } from '../utils/dateUtils';
import { ScheduleJob } from '../hooks/useScheduleData';

interface Customer {
  _id: string;
  id?: string;
  firstName: string;
  lastName: string;
  linkedCustomerId?: string | {
    _id?: string;
    id?: string;
  };
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

interface CustomerScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (updatedService?: any) => void;
  defaultDate?: string; // Optional: pre-fill with a specific date (e.g., today)
  onEditSchedule?: (job: ScheduleJob) => void; // Reuse the same edit handler from ProviderDashboard
}

/**
 * Dialog for editing a customer's schedule
 * Uses the same approach as "This Week's Schedule" - fetches actual scheduled jobs
 * and displays them, allowing direct editing like the schedule view
 */
const CustomerScheduleDialog: React.FC<CustomerScheduleDialogProps> = ({
  open,
  onClose,
  onSuccess,
  defaultDate,
  onEditSchedule,
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerJobs, setCustomerJobs] = useState<ScheduleJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCustomerInputFocused, setIsCustomerInputFocused] = useState(false);
  const [customerNextServiceDate, setCustomerNextServiceDate] = useState<Date | null>(null);
  const [customerServiceSchedule, setCustomerServiceSchedule] = useState<any>(null); // Store full customer for serviceSchedule
  const [filterBy, setFilterBy] = useState<'all' | 'lawnMowing' | 'weedControl' | 'snowRemoval' | 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance' | 'seasonal-contract'>('all');

  // Fetch customers when dialog opens
  useEffect(() => {
    if (open) {
      fetchCustomers();
    } else {
      // Reset state when dialog closes
      setSelectedCustomer(null);
      setCustomerJobs([]);
      setCustomerNextServiceDate(null);
      setCustomerServiceSchedule(null);
      setError(null);
      setIsCustomerInputFocused(false);
      setFilterBy('all');
    }
  }, [open]);

  // Fetch customer's scheduled jobs when customer is selected
  useEffect(() => {
    if (selectedCustomer && open) {
      fetchCustomerJobs();
    } else {
      setCustomerJobs([]);
      setCustomerNextServiceDate(null);
      setCustomerServiceSchedule(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, open]);

  const fetchCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const response = await apiService.get('/existing-customers');
      if (response.success && response.data) {
        const customersArray = Array.isArray(response.data) ? response.data : [];
        setCustomers(customersArray);
        console.log('[CUSTOMER-SCHEDULE] Loaded customers:', customersArray.length);
      } else {
        console.error('[CUSTOMER-SCHEDULE] Failed to fetch customers - no success:', response);
        setError('Failed to load customers');
      }
    } catch (err) {
      console.error('[CUSTOMER-SCHEDULE] Failed to fetch customers:', err);
      setError('Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchCustomerJobs = async () => {
    if (!selectedCustomer) return;

    try {
      setLoadingJobs(true);
      setError(null);

      // Get the property-specific customerId (ExistingCustomer._id) - this is the specific property
      const propertyCustomerId = (selectedCustomer._id || selectedCustomer.id)?.toString();
      const propertyAddress = selectedCustomer.address;

      if (!propertyCustomerId) {
        setError('Customer does not have a valid ID');
        return;
      }

      // Normalize address for comparison
      const normalizeAddress = (addr: any) => {
        if (!addr) return null;
        return {
          street: (addr.street || '').trim().toLowerCase(),
          zipCode: (addr.zipCode || '').trim()
        };
      };

      const normalizedPropertyAddress = normalizeAddress(propertyAddress);

      console.log('[CUSTOMER-SCHEDULE] Fetching jobs for property:', {
        propertyCustomerId: propertyCustomerId,
        customerName: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
        address: propertyAddress ? `${propertyAddress.street}, ${propertyAddress.zipCode}` : 'N/A',
        normalizedAddress: normalizedPropertyAddress
      });

      // Fetch BOTH:
      // 1. Scheduled jobs (with scheduledDate) from schedule endpoint
      // 2. Accepted service requests (without scheduledDate) from service-requests endpoint
      // Use the same date range calculation as useScheduleData hook
      const todayStr = getTodayInUserTimezone();
      const [todayYear, todayMonth, todayDay] = todayStr.split('-').map(Number);
      const todayDayOfWeek = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
      
      // Calculate start of week (Monday) - same as useScheduleData
      let daysFromMonday;
      if (todayDayOfWeek === 0) {
        daysFromMonday = -1; // Sunday: Show Monday-Saturday of NEXT week
      } else {
        daysFromMonday = todayDayOfWeek - 1; // Monday-Saturday: Calculate days back to THIS week's Monday
      }
      
      const startDate = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay - daysFromMonday, 0, 0, 0, 0));
      const endDate = new Date(Date.UTC(todayYear, todayMonth + 2, todayDay, 23, 59, 59, 999)); // 3 months from now

      console.log('[CUSTOMER-SCHEDULE] Date range calculation:', {
        todayStr,
        todayYear,
        todayMonth,
        todayDay,
        todayDayOfWeek,
        daysFromMonday,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });

      // Fetch scheduled jobs
      const scheduleResponse = await apiService.get(
        `/service-requests/provider/schedule?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
      );

      console.log('[CUSTOMER-SCHEDULE] Schedule API response:', {
        success: scheduleResponse.success,
        hasData: !!scheduleResponse.data,
        dataType: typeof scheduleResponse.data,
        isArray: Array.isArray(scheduleResponse.data),
        dataLength: Array.isArray(scheduleResponse.data) ? scheduleResponse.data.length : 'N/A',
        dataKeys: scheduleResponse.data && !Array.isArray(scheduleResponse.data) ? Object.keys(scheduleResponse.data) : [],
        firstItem: Array.isArray(scheduleResponse.data) && scheduleResponse.data.length > 0 ? scheduleResponse.data[0] : null
      });

      // Fetch ALL service requests for the provider (we'll filter by customerId on frontend)
      // IMPORTANT: Include 'completed' status to find services that have been completed but have nextServiceDate
      // The schedule endpoint already gives us scheduled services, so we need accepted/in-progress/completed
      const allServiceRequestsResponse = await apiService.get(
        `/service-requests?userType=provider&status=accepted,scheduled,in-progress,completed&limit=1000&page=1`
      );

      console.log('[CUSTOMER-SCHEDULE] All ServiceRequests API response:', {
        success: allServiceRequestsResponse.success,
        hasData: !!allServiceRequestsResponse.data,
        dataType: typeof allServiceRequestsResponse.data,
        isArray: Array.isArray(allServiceRequestsResponse.data),
        dataLength: Array.isArray(allServiceRequestsResponse.data) ? allServiceRequestsResponse.data.length : 'N/A',
        dataKeys: allServiceRequestsResponse.data && !Array.isArray(allServiceRequestsResponse.data) ? Object.keys(allServiceRequestsResponse.data) : [],
        firstItem: Array.isArray(allServiceRequestsResponse.data) && allServiceRequestsResponse.data.length > 0 ? allServiceRequestsResponse.data[0] : null
      });

      // Also fetch all ExistingCustomer records for the provider (we'll filter by linkedCustomerId on frontend)
      const existingCustomerResponse = await apiService.get(
        `/existing-customers`
      );

      console.log('[CUSTOMER-SCHEDULE] ExistingCustomer API response:', {
        success: existingCustomerResponse.success,
        hasData: !!existingCustomerResponse.data,
        dataType: typeof existingCustomerResponse.data,
        isArray: Array.isArray(existingCustomerResponse.data),
        dataLength: Array.isArray(existingCustomerResponse.data) ? existingCustomerResponse.data.length : 'N/A'
      });

      const allJobs: any[] = [];

      // Add scheduled jobs - match useScheduleData hook format (response.data is array directly)
      // CRITICAL: Filter by property-specific customerId AND address to only show services for this property
      if (scheduleResponse.success && scheduleResponse.data) {
        // The schedule endpoint returns the array directly in response.data (like useScheduleData expects)
        const allScheduledJobs = Array.isArray(scheduleResponse.data) 
          ? scheduleResponse.data 
          : [];
        
        // Filter to only jobs for this specific property
        // CRITICAL: Use existingCustomerId (property-specific) when available, fallback to address matching
        console.log('[CUSTOMER-SCHEDULE] Filtering scheduled jobs:', {
          totalScheduledJobs: allScheduledJobs.length,
          propertyCustomerId,
          normalizedPropertyAddress,
          sampleJobs: allScheduledJobs.slice(0, 5).map((j: any) => ({
            id: j._id || j.id,
            existingCustomerId: j.existingCustomerId?._id || j.existingCustomerId?.id || j.existingCustomerId,
            address: j.address ? `${j.address.street}, ${j.address.zipCode}` : 'N/A',
            serviceType: j.serviceType?.category || j.serviceType?.name
          }))
        });
        
        const scheduledJobs = allScheduledJobs.filter((j: any) => {
          // PRIMARY: Match by existingCustomerId (property-specific) - this is the proper way
          const jobExistingCustomerId = (j.existingCustomerId?._id || j.existingCustomerId?.id || j.existingCustomerId)?.toString();
          if (jobExistingCustomerId && jobExistingCustomerId === propertyCustomerId) {
            console.log('[CUSTOMER-SCHEDULE] ✓ Scheduled job matched by existingCustomerId:', {
              jobId: j._id || j.id,
              serviceType: j.serviceType?.category || j.serviceType?.name
            });
            return true; // Property-specific match - this is the correct way
          }
          
          // FALLBACK: For older ServiceRequests without existingCustomerId, match by address
          const jobAddress = normalizeAddress(j.address || j.customerId?.address);
          const addressMatches = normalizedPropertyAddress && jobAddress
            ? jobAddress.street === normalizedPropertyAddress.street && 
              jobAddress.zipCode === normalizedPropertyAddress.zipCode
            : false;
          
          if (addressMatches) {
            console.log('[CUSTOMER-SCHEDULE] ✓ Scheduled job matched by address:', {
              jobId: j._id || j.id,
              serviceType: j.serviceType?.category || j.serviceType?.name,
              jobAddress: jobAddress ? `${jobAddress.street}, ${jobAddress.zipCode}` : 'N/A',
              propertyAddress: normalizedPropertyAddress ? `${normalizedPropertyAddress.street}, ${normalizedPropertyAddress.zipCode}` : 'N/A'
            });
            return true;
          }
          
          // Log if filtering out
          if (allScheduledJobs.length < 20) {
            console.log('[CUSTOMER-SCHEDULE] ✗ Scheduled job filtered out (not for this property):', {
              jobId: j._id || j.id,
              jobExistingCustomerId,
              propertyCustomerId,
              existingCustomerIdMatches: jobExistingCustomerId === propertyCustomerId,
              jobAddress: jobAddress ? `${jobAddress.street}, ${jobAddress.zipCode}` : 'N/A',
              propertyAddress: normalizedPropertyAddress ? `${normalizedPropertyAddress.street}, ${normalizedPropertyAddress.zipCode}` : 'N/A',
              addressMatches,
              serviceType: j.serviceType?.category || j.serviceType?.name
            });
          }
          
          return false;
        });
        
        allJobs.push(...scheduledJobs);
        console.log('[CUSTOMER-SCHEDULE] Found scheduled jobs for property:', {
          total: scheduledJobs.length,
          filteredFrom: allScheduledJobs.length,
          propertyCustomerId,
          sampleJobs: scheduledJobs.slice(0, 3).map((j: any) => ({
            jobId: j._id || j.id,
            customerId: j.customerId?._id || j.customerId?.id || j.customerId,
            address: j.address ? `${j.address.street}, ${j.address.zipCode}` : 'N/A',
            serviceType: j.serviceType?.category || j.serviceType?.name
          }))
        });
      }

      // Add all service requests (any status except cancelled) - filter by customerId on frontend
      // IMPORTANT: This includes ALL service requests, not just those in the date range
      // This ensures we find scheduled services that might be outside the current date range
      if (allServiceRequestsResponse.success && allServiceRequestsResponse.data) {
        // Try different possible response structures
        let serviceRequests: any[] = [];
        const serviceRequestsData = allServiceRequestsResponse.data as any;
        
        console.log('[CUSTOMER-SCHEDULE] Raw serviceRequestsData structure:', {
          isArray: Array.isArray(serviceRequestsData),
          hasServiceRequests: !!serviceRequestsData.serviceRequests,
          serviceRequestsType: Array.isArray(serviceRequestsData.serviceRequests) ? 'array' : typeof serviceRequestsData.serviceRequests,
          serviceRequestsLength: Array.isArray(serviceRequestsData.serviceRequests) ? serviceRequestsData.serviceRequests.length : 'N/A',
          keys: Object.keys(serviceRequestsData),
          hasData: !!serviceRequestsData.data,
          dataType: serviceRequestsData.data ? (Array.isArray(serviceRequestsData.data) ? 'array' : typeof serviceRequestsData.data) : 'N/A'
        });
        
        // The endpoint returns: { success: true, data: { serviceRequests: [...], pagination: {...} } }
        // So allServiceRequestsResponse.data = { serviceRequests: [...], pagination: {...} }
        if (Array.isArray(serviceRequestsData)) {
          serviceRequests = serviceRequestsData;
          console.log('[CUSTOMER-SCHEDULE] Using direct array');
        } else if (serviceRequestsData.serviceRequests && Array.isArray(serviceRequestsData.serviceRequests)) {
          serviceRequests = serviceRequestsData.serviceRequests;
          console.log('[CUSTOMER-SCHEDULE] Using serviceRequestsData.serviceRequests, found', serviceRequests.length, 'requests');
        } else if (serviceRequestsData.data) {
          if (Array.isArray(serviceRequestsData.data)) {
            serviceRequests = serviceRequestsData.data;
            console.log('[CUSTOMER-SCHEDULE] Using serviceRequestsData.data (array)');
          } else if (serviceRequestsData.data.serviceRequests && Array.isArray(serviceRequestsData.data.serviceRequests)) {
            serviceRequests = serviceRequestsData.data.serviceRequests;
            console.log('[CUSTOMER-SCHEDULE] Using serviceRequestsData.data.serviceRequests');
          }
        }
        
        console.log('[CUSTOMER-SCHEDULE] Parsed service requests:', {
          totalParsed: serviceRequests.length,
          sampleIds: serviceRequests.slice(0, 5).map((req: any) => ({
            id: req._id || req.id,
            status: req.status,
            scheduledDate: req.scheduledDate,
            serviceType: req.serviceType?.category || req.serviceType?.name,
            customerId: req.customerId?._id || req.customerId?.id || req.customerId
          }))
        });
        
        // CRITICAL: Filter by existingCustomerId (property-specific) when available, fallback to address
        console.log('[CUSTOMER-SCHEDULE] Filtering ServiceRequests:', {
          totalServiceRequests: serviceRequests.length,
          propertyCustomerId,
          normalizedPropertyAddress,
          sampleServiceRequests: serviceRequests.slice(0, 5).map((req: any) => ({
            id: req._id || req.id,
            existingCustomerId: req.existingCustomerId?._id || req.existingCustomerId?.id || req.existingCustomerId,
            address: req.address ? `${req.address.street}, ${req.address.zipCode}` : 'N/A',
            serviceType: req.serviceType?.category || req.serviceType?.name
          }))
        });
        
        const customerServiceRequests = serviceRequests.filter((req: any) => {
          // PRIMARY: Match by existingCustomerId (property-specific) - this is the proper way
          let reqExistingCustomerId: string | null = null;
          if (req.existingCustomerId) {
            if (typeof req.existingCustomerId === 'object') {
              reqExistingCustomerId = (req.existingCustomerId._id || req.existingCustomerId.id)?.toString() || null;
            } else {
              reqExistingCustomerId = req.existingCustomerId.toString();
            }
          }
          
          if (reqExistingCustomerId && reqExistingCustomerId === propertyCustomerId) {
            console.log('[CUSTOMER-SCHEDULE] ✓ ServiceRequest matched by existingCustomerId:', {
              reqId: req._id || req.id,
              serviceType: req.serviceType?.category || req.serviceType?.name
            });
            return true; // Property-specific match - this is the correct way
          }
          
          // FALLBACK: For older ServiceRequests without existingCustomerId, match by address
          const reqAddress = normalizeAddress(req.address || req.customerId?.address);
          const addressMatches = normalizedPropertyAddress && reqAddress
            ? reqAddress.street === normalizedPropertyAddress.street && 
              reqAddress.zipCode === normalizedPropertyAddress.zipCode
            : false;
          
          if (addressMatches) {
            console.log('[CUSTOMER-SCHEDULE] ✓ ServiceRequest matched by address:', {
              reqId: req._id || req.id,
              serviceType: req.serviceType?.category || req.serviceType?.name,
              reqAddress: reqAddress ? `${reqAddress.street}, ${reqAddress.zipCode}` : 'N/A',
              propertyAddress: normalizedPropertyAddress ? `${normalizedPropertyAddress.street}, ${normalizedPropertyAddress.zipCode}` : 'N/A'
            });
            return true;
          }
          
          // Log if filtering out (for debugging)
          if (serviceRequests.length < 20) {
            console.log('[CUSTOMER-SCHEDULE] ✗ ServiceRequest filtered out (not for this property):', {
              reqId: req._id || req.id,
              reqExistingCustomerId,
              propertyCustomerId,
              existingCustomerIdMatches: reqExistingCustomerId === propertyCustomerId,
              reqAddress: reqAddress ? `${reqAddress.street}, ${reqAddress.zipCode}` : 'N/A',
              propertyAddress: normalizedPropertyAddress ? `${normalizedPropertyAddress.street}, ${normalizedPropertyAddress.zipCode}` : 'N/A',
              addressMatches,
              serviceType: req.serviceType?.category || req.serviceType?.name
            });
          }
          
          return false;
        });
        
        console.log('[CUSTOMER-SCHEDULE] ServiceRequests for customer after filtering:', {
          total: customerServiceRequests.length,
          services: customerServiceRequests.map((req: any) => ({
            id: req._id || req.id,
            serviceType: req.serviceType?.category || req.serviceType?.name,
            scheduledDate: req.scheduledDate,
            status: req.status
          }))
        });
        
        // Filter out cancelled services and those already in scheduledJobs
        const scheduledJobIds = new Set(
          allJobs.map((j: any) => (j._id || j.id)?.toString()).filter(Boolean)
        );
        
        const additionalServiceRequests = customerServiceRequests.filter((req: any) => {
          const reqId = (req._id || req.id)?.toString();
          const isCancelled = req.status === 'cancelled' || req.status === 'pending_cancellation';
          const alreadyInScheduled = scheduledJobIds.has(reqId);
          if (isCancelled || alreadyInScheduled) {
            console.log('[CUSTOMER-SCHEDULE] ServiceRequest filtered out:', {
              reqId,
              reason: isCancelled ? 'cancelled' : 'already in scheduled jobs',
              status: req.status
            });
          }
          return !isCancelled && !alreadyInScheduled;
        });
        
        allJobs.push(...additionalServiceRequests);
        console.log('[CUSTOMER-SCHEDULE] Found additional service requests for customer:', additionalServiceRequests.length);
        if (additionalServiceRequests.length > 0) {
          console.log('[CUSTOMER-SCHEDULE] Sample additional service requests:', additionalServiceRequests.slice(0, 3).map((req: any) => ({
            id: req._id || req.id,
            serviceType: req.serviceType?.name || req.serviceType?.category,
            scheduledDate: req.scheduledDate,
            status: req.status,
            address: req.address
          })));
        }
      }

      // Add services from ExistingCustomer records that don't have ServiceRequest records yet
      // CRITICAL: Filter to only the selected property (match by _id and address)
      if (existingCustomerResponse.success && existingCustomerResponse.data) {
        const allExistingCustomers = Array.isArray(existingCustomerResponse.data) 
          ? existingCustomerResponse.data 
          : [];
        
        // Filter to only the selected property (match by property-specific _id)
        const existingCustomers = allExistingCustomers.filter((ec: any) => {
          const ecId = (ec._id || ec.id)?.toString();
          const ecAddress = normalizeAddress(ec.address);
          
          // Match by property-specific ID
          const idMatches = ecId === propertyCustomerId;
          
          // Also match by address for extra safety
          const addressMatches = normalizedPropertyAddress && ecAddress
            ? ecAddress.street === normalizedPropertyAddress.street && 
              ecAddress.zipCode === normalizedPropertyAddress.zipCode
            : true; // If address not available, rely on ID match
          
          return idMatches && addressMatches;
        });
        
        console.log('[CUSTOMER-SCHEDULE] Found ExistingCustomer records for customer:', existingCustomers.length);
        if (existingCustomers.length > 0) {
          console.log('[CUSTOMER-SCHEDULE] ExistingCustomer record details:', {
            _id: existingCustomers[0]._id,
            address: existingCustomers[0].address,
            servicePricing: existingCustomers[0].servicePricing ? Object.keys(existingCustomers[0].servicePricing).map(key => ({
              serviceKey: key,
              hasPrice: !!existingCustomers[0].servicePricing[key]?.price,
              price: existingCustomers[0].servicePricing[key]?.price,
              frequency: existingCustomers[0].servicePricing[key]?.frequency
            })) : []
          });
        }
        
        // Store the customer's full record (including serviceSchedule) for calculating next service date
        if (existingCustomers.length > 0) {
          const customer = existingCustomers[0];
          setCustomerServiceSchedule(customer.serviceSchedule || null);
          
          // Calculate next service date using same logic as All Customers chart
          // This ensures consistency across all UI components
          const calculateNextServiceDate = (completedDate: Date | string, frequency: string, dayOfWeek?: number): Date | null => {
            if (!completedDate) return null;
            
            const completed = typeof completedDate === 'string' ? new Date(completedDate) : completedDate;
            const year = completed.getUTCFullYear();
            const month = completed.getUTCMonth();
            const day = completed.getUTCDate();
            
            let nextYear = year;
            let nextMonth = month;
            let nextDay = day;
            
            if (frequency === 'weekly') {
              if (dayOfWeek !== undefined && dayOfWeek !== null) {
                const completedDayOfWeek = completed.getUTCDay();
                let daysToAdd = dayOfWeek - completedDayOfWeek;
                if (daysToAdd <= 0) {
                  daysToAdd += 7; // Next week
                }
                nextDay += daysToAdd;
              } else {
                nextDay += 7;
              }
            } else if (frequency === 'bi-weekly') {
              if (dayOfWeek !== undefined && dayOfWeek !== null) {
                const completedDayOfWeek = completed.getUTCDay();
                let daysToAdd = dayOfWeek - completedDayOfWeek;
                if (daysToAdd <= 0) {
                  daysToAdd += 14; // Next bi-weekly cycle
                } else {
                  daysToAdd += 7; // Next week (same day, but we need 2 weeks total)
                }
                nextDay += daysToAdd;
              } else {
                nextDay += 14;
              }
            } else if (frequency === 'monthly') {
              nextMonth += 1;
              if (nextMonth > 11) {
                nextMonth = 0;
                nextYear += 1;
              }
            } else {
              return null; // per-instance or seasonal-contract
            }
            
            return new Date(Date.UTC(nextYear, nextMonth, nextDay, 0, 0, 0, 0));
          };
          
          // Find the most recent completed service request for this customer
          const completedJob = allJobs
            .filter((j: any) => j.status === 'completed' && j.completedDate)
            .sort((a: any, b: any) => {
              const dateA = new Date(a.completedDate).getTime();
              const dateB = new Date(b.completedDate).getTime();
              return dateB - dateA; // Most recent first
            })[0];
          
          if (completedJob && customer.serviceSchedule) {
            const frequency = customer.serviceSchedule.frequency;
            const dayOfWeek = customer.serviceSchedule.dayOfWeek;
            const calculatedNextDate = calculateNextServiceDate(completedJob.completedDate, frequency, dayOfWeek);
            
            if (calculatedNextDate) {
              setCustomerNextServiceDate(calculatedNextDate);
              console.log('[CUSTOMER-SCHEDULE] Calculated nextServiceDate (matching All Customers chart):', {
                completedDate: completedJob.completedDate,
                frequency,
                dayOfWeek,
                calculatedNextDate: calculatedNextDate.toISOString(),
                backendNextServiceDate: customer.nextServiceDate
              });
            } else {
              // Fallback to backend value
              setCustomerNextServiceDate(customer.nextServiceDate ? new Date(customer.nextServiceDate) : null);
            }
          } else {
            // No completed job or no service schedule - use backend value
            setCustomerNextServiceDate(customer.nextServiceDate ? new Date(customer.nextServiceDate) : null);
          }
        } else {
          setCustomerServiceSchedule(null);
          setCustomerNextServiceDate(null);
        }
        
        // Get all existing ServiceRequest IDs to avoid duplicates
        const existingServiceRequestIds = new Set(
          allJobs.map((j: any) => (j._id || j.id)?.toString()).filter(Boolean)
        );
        
        // Create virtual ServiceRequests from ExistingCustomer records
        existingCustomers.forEach((ec: any) => {
          const servicePricing = ec.servicePricing || {};
          
          // Check each service type
          ['lawnMowingTrimming', 'weedControlFertilizing', 'snowRemoval'].forEach((serviceKey) => {
            const service = servicePricing[serviceKey];
            console.log('[CUSTOMER-SCHEDULE] Checking service:', {
              serviceKey,
              hasService: !!service,
              hasPrice: !!service?.price,
              price: service?.price,
              priceType: typeof service?.price,
              priceValue: service?.price,
              priceIsZero: service?.price === 0,
              priceIsFalsy: !service?.price,
              frequency: service?.frequency,
              fullService: service
            });
            
            // Check if service exists and has a valid price (must be a number > 0)
            // For seasonal contracts, price should still be set (it's the total contract price)
            const hasValidPrice = service && 
              service.price !== undefined && 
              service.price !== null && 
              typeof service.price === 'number' && 
              service.price > 0;
            
            if (!service || !hasValidPrice) {
              console.log('[CUSTOMER-SCHEDULE] Skipping service:', {
                serviceKey,
                reason: !service ? 'no service object' : 'invalid price',
                serviceExists: !!service,
                priceValue: service?.price,
                priceType: typeof service?.price,
                priceIsZero: service?.price === 0,
                priceIsNull: service?.price === null,
                priceIsUndefined: service?.price === undefined
              });
              return; // Skip if service not enabled or no valid price
            }
            
            // Map serviceKey to service category (must match backend logic)
            const serviceKeyMap: { [key: string]: string } = {
              'lawnMowingTrimming': 'mowing',
              'weedControlFertilizing': 'fertilizing',
              'snowRemoval': 'snow_removal'
            };
            const expectedCategory = serviceKeyMap[serviceKey];
            
            // Check if we already have a ServiceRequest for this service
            // Must match backend logic: customerId, address (street + zipCode), serviceType.category, status != 'cancelled'
            // IMPORTANT: Check ALL jobs (scheduled + all service requests) to find existing ServiceRequests
            console.log('[CUSTOMER-SCHEDULE] Checking for existing ServiceRequest:', {
              serviceKey,
              expectedCategory,
              propertyCustomerId,
              allJobsCount: allJobs.length,
              allJobsCategories: allJobs.map((j: any) => ({
                id: j._id || j.id,
                category: j.serviceType?.category,
                name: j.serviceType?.name,
                customerId: j.customerId?._id || j.customerId?.id || j.customerId,
                status: j.status,
                scheduledDate: j.scheduledDate
              }))
            });
            
            const hasServiceRequest = allJobs.some((job: any) => {
              // Check service category matches
              const jobServiceCategory = job.serviceType?.category;
              const categoryMatches = jobServiceCategory === expectedCategory;
              
              // Check status is not cancelled
              const notCancelled = job.status !== 'cancelled' && job.status !== 'pending_cancellation';
              
              // Check customerId matches (property-specific ExistingCustomer._id)
              let jobCustomerId: string | null = null;
              if (job.customerId) {
                if (typeof job.customerId === 'object') {
                  jobCustomerId = (job.customerId._id || job.customerId.id)?.toString() || null;
                } else {
                  jobCustomerId = job.customerId.toString();
                }
              }
              
              // PRIMARY: Match by existingCustomerId (property-specific) - this is the proper way
              let jobExistingCustomerId: string | null = null;
              if (job.existingCustomerId) {
                if (typeof job.existingCustomerId === 'object') {
                  jobExistingCustomerId = (job.existingCustomerId._id || job.existingCustomerId.id)?.toString() || null;
                } else {
                  jobExistingCustomerId = job.existingCustomerId.toString();
                }
              }
              
              const existingCustomerIdMatches = jobExistingCustomerId === propertyCustomerId;
              
              // FALLBACK: For older ServiceRequests without existingCustomerId, match by address
              let addressMatches = false;
              if (ec.address && job.address) {
                const jobStreet = (job.address.street || '').trim().toLowerCase();
                const jobZipCode = (job.address.zipCode || '').trim();
                const ecStreet = (ec.address.street || '').trim().toLowerCase();
                const ecZipCode = (ec.address.zipCode || '').trim();
                
                addressMatches = jobStreet === ecStreet && jobZipCode === ecZipCode;
              }
              
              // Match if category matches, not cancelled, and (existingCustomerId matches OR address matches)
              // existingCustomerId is primary (property-specific), address is fallback for older records
              const matches = categoryMatches && notCancelled && (existingCustomerIdMatches || addressMatches);
              
              // Log detailed matching info for jobs with same category
              if (jobServiceCategory === expectedCategory) {
                console.log('[CUSTOMER-SCHEDULE] Job with matching category:', {
                  jobId: job._id || job.id,
                  category: jobServiceCategory,
                  expectedCategory,
                  categoryMatches,
                  notCancelled,
                  jobCustomerId,
                  jobExistingCustomerId,
                  propertyCustomerId,
                  existingCustomerIdMatches,
                  jobAddress: job.address ? `${job.address.street}, ${job.address.zipCode}` : 'N/A',
                  ecAddress: ec.address ? `${ec.address.street}, ${ec.address.zipCode}` : 'N/A',
                  addressMatches,
                  status: job.status,
                  matches
                });
              }
              
              if (matches) {
                // All checks passed - this is a matching ServiceRequest
                console.log('[CUSTOMER-SCHEDULE] ✓ Found matching ServiceRequest for virtual service:', {
                  serviceKey,
                  jobId: job._id || job.id,
                  scheduledDate: job.scheduledDate,
                  status: job.status,
                  address: job.address,
                  jobCustomerId,
                  propertyCustomerId
                });
                return true;
              }
              
              return false;
            });
            
            if (!hasServiceRequest) {
              // Log detailed info about why no match was found
              const jobsWithSameCategory = allJobs.filter((j: any) => {
                const jCategory = j.serviceType?.category;
                return jCategory === expectedCategory;
              });
              
              console.log('[CUSTOMER-SCHEDULE] No matching ServiceRequest found for virtual service:', {
                serviceKey,
                expectedCategory,
                propertyCustomerId,
                ecAddress: ec.address,
                allJobsCount: allJobs.length,
                jobsWithSameCategoryCount: jobsWithSameCategory.length,
                jobsWithSameCategory: jobsWithSameCategory.map((j: any) => {
                  let jCustomerId: string | null = null;
                  if (j.customerId) {
                    if (typeof j.customerId === 'object') {
                      jCustomerId = (j.customerId._id || j.customerId.id)?.toString() || null;
                    } else {
                      jCustomerId = j.customerId.toString();
                    }
                  }
                  return {
                    id: j._id || j.id,
                    category: j.serviceType?.category,
                    customerId: jCustomerId,
                    customerIdMatches: jCustomerId === propertyCustomerId,
                    address: j.address,
                    scheduledDate: j.scheduledDate,
                    status: j.status
                  };
                })
              });
            }
            
            if (!hasServiceRequest) {
              // Create a virtual ServiceRequest for this service
              const serviceTypeMap: Record<string, any> = {
                'lawnMowingTrimming': { category: 'mowing', name: 'Lawn Mowing/Trimming' },
                'weedControlFertilizing': { category: 'fertilizing', name: 'Weed Control/Fertilizing' },
                'snowRemoval': { category: 'snow_removal', name: 'Snow Removal' }
              };
              
              const virtualServiceRequest = {
                _id: `virtual-${ec._id}-${serviceKey}`,
                id: `virtual-${ec._id}-${serviceKey}`,
                customerId: propertyCustomerId, // Use property-specific customerId
                serviceType: serviceTypeMap[serviceKey],
                address: ec.address,
                status: 'accepted', // These are accepted but not yet scheduled
                scheduledDate: null,
                frequency: service.frequency || 'weekly',
                estimatedDuration: 60, // Default
                isVirtual: true, // Flag to indicate this is a virtual ServiceRequest
                existingCustomerId: ec._id,
                serviceKey: serviceKey
              };
              
              allJobs.push(virtualServiceRequest);
              console.log('[CUSTOMER-SCHEDULE] Created virtual ServiceRequest:', {
                serviceKey,
                address: ec.address,
                frequency: service.frequency
              });
            }
          });
        });
        
        console.log('[CUSTOMER-SCHEDULE] Added virtual ServiceRequests from ExistingCustomer records');
      }

      console.log('[CUSTOMER-SCHEDULE] Total jobs before filtering:', allJobs.length);
      console.log('[CUSTOMER-SCHEDULE] Looking for property with customerId:', propertyCustomerId);

      // CRITICAL: Filter to only this property's jobs (by existingCustomerId OR address)
      const filteredJobs = allJobs.filter((job: any) => {
        // PRIMARY: Match by existingCustomerId (property-specific) - this is the proper way
        let jobExistingCustomerId: string | null = null;
        if (job.existingCustomerId) {
          if (typeof job.existingCustomerId === 'object') {
            jobExistingCustomerId = (job.existingCustomerId._id || job.existingCustomerId.id)?.toString() || null;
          } else {
            jobExistingCustomerId = job.existingCustomerId.toString();
          }
        }
        
        if (jobExistingCustomerId && jobExistingCustomerId === propertyCustomerId) {
          console.log('[CUSTOMER-SCHEDULE] ✓ Final filter: Job matched by existingCustomerId:', {
            jobId: job._id || job.id,
            serviceType: job.serviceType?.category || job.serviceType?.name
          });
          return true; // Property-specific match - this is the correct way
        }
        
        // FALLBACK: For older ServiceRequests without existingCustomerId, match by address
        const jobAddress = normalizeAddress(job.address || job.customerId?.address);
        const addressMatches = normalizedPropertyAddress && jobAddress
          ? jobAddress.street === normalizedPropertyAddress.street && 
            jobAddress.zipCode === normalizedPropertyAddress.zipCode
          : false;
        
        if (addressMatches) {
          console.log('[CUSTOMER-SCHEDULE] ✓ Final filter: Job matched by address:', {
            jobId: job._id || job.id,
            serviceType: job.serviceType?.category || job.serviceType?.name,
            jobAddress: jobAddress ? `${jobAddress.street}, ${jobAddress.zipCode}` : 'N/A',
            propertyAddress: normalizedPropertyAddress ? `${normalizedPropertyAddress.street}, ${normalizedPropertyAddress.zipCode}` : 'N/A'
          });
          return true;
        }
        
        // Log if filtering out
        if (allJobs.length < 20) {
          console.log('[CUSTOMER-SCHEDULE] ✗ Final filter: Job filtered out (not for this property):', {
            jobId: job._id || job.id,
            jobExistingCustomerId,
            propertyCustomerId,
            existingCustomerIdMatches: jobExistingCustomerId === propertyCustomerId,
            jobAddress: jobAddress ? `${jobAddress.street}, ${jobAddress.zipCode}` : 'N/A',
            propertyAddress: normalizedPropertyAddress ? `${normalizedPropertyAddress.street}, ${normalizedPropertyAddress.zipCode}` : 'N/A',
            addressMatches,
            serviceType: job.serviceType?.category || job.serviceType?.name
          });
        }
        
        return false;
      });

      // Remove duplicates (in case a job appears in both lists)
      const uniqueJobs = filteredJobs.filter((job: any, index: number, self: any[]) => {
        const jobId = (job._id || job.id)?.toString();
        return index === self.findIndex((j: any) => (j._id || j.id)?.toString() === jobId);
      });

      const totalScheduled = scheduleResponse.success && Array.isArray(scheduleResponse.data) 
        ? scheduleResponse.data.length 
        : 0;
      const totalServiceRequests = allServiceRequestsResponse.success && Array.isArray(allServiceRequestsResponse.data)
        ? allServiceRequestsResponse.data.length
        : 0;

      console.log('[CUSTOMER-SCHEDULE] Filtered jobs for customer:', {
        totalScheduled,
        totalServiceRequests,
        customerJobs: uniqueJobs.length,
        jobs: uniqueJobs.map((j: any) => ({
          id: j._id || j.id,
          serviceType: j.serviceType?.name || j.serviceType?.category,
          scheduledDate: j.scheduledDate,
          status: j.status
        }))
      });

      setCustomerJobs(uniqueJobs);
    } catch (err: any) {
      console.error('[CUSTOMER-SCHEDULE] Failed to fetch customer jobs:', err);
      setError(err.message || 'Failed to load customer jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleEditJob = async (job: ScheduleJob) => {
    // Check if this is a virtual ServiceRequest (from ExistingCustomer record)
    const isVirtual = (job as any).isVirtual === true;
    
    if (isVirtual) {
      // For virtual services, we need to create a real ServiceRequest first
      const virtualJob = job as any;
      const existingCustomerId = virtualJob.existingCustomerId;
      const serviceKey = virtualJob.serviceKey;
      
      if (!existingCustomerId || !serviceKey) {
        setError('Cannot edit virtual service: Missing required information');
        return;
      }
      
      try {
        setLoadingJobs(true);
        setError(null);
        
        // Create a real ServiceRequest from the ExistingCustomer record
        const createResponse = await apiService.post('/service-requests/from-existing-customer', {
          existingCustomerId: existingCustomerId,
          serviceKey: serviceKey,
          scheduledDate: null // Will be set when user schedules it
        });
        
        if (createResponse.success) {
          const createdServiceRequest = (createResponse.data as any).serviceRequest || createResponse.data;
          
          // Update the job with the real ServiceRequest ID
          const realJob = {
            ...job,
            _id: createdServiceRequest._id || createdServiceRequest.id,
            id: createdServiceRequest._id || createdServiceRequest.id,
            isVirtual: false
          };
          
          // Refresh the jobs list to show the real ServiceRequest
          await fetchCustomerJobs();
          
          // Now call the edit handler with the real ServiceRequest
          if (onEditSchedule) {
            onEditSchedule(realJob);
            onClose();
          }
        } else {
          // Check if the error is because the ServiceRequest already exists
          // The backend returns the existing ServiceRequest in createResponse.data when it already exists
          const existingServiceRequest = createResponse.data as any;
          if ((createResponse.error === 'ServiceRequest already exists' || createResponse.message?.includes('already exists')) && existingServiceRequest) {
            // The backend returned the existing ServiceRequest - use it instead
            const realJob = {
              ...job,
              _id: existingServiceRequest._id || existingServiceRequest.id,
              id: existingServiceRequest._id || existingServiceRequest.id,
              isVirtual: false,
              ...existingServiceRequest // Include all properties from the real ServiceRequest
            };
            
            // Refresh the jobs list to show the real ServiceRequest
            await fetchCustomerJobs();
            
            // Now call the edit handler with the existing ServiceRequest
            if (onEditSchedule) {
              onEditSchedule(realJob);
              onClose();
            }
          } else {
            setError(createResponse.error || createResponse.message || 'Failed to create service request. Please try again.');
          }
        }
      } catch (err: any) {
        console.error('[CUSTOMER-SCHEDULE] Error creating ServiceRequest from virtual:', err);
        console.log('[CUSTOMER-SCHEDULE] Error structure:', {
          hasResponse: !!err.response,
          hasRaw: !!err.raw,
          rawType: typeof err.raw,
          rawKeys: err.raw ? Object.keys(err.raw) : [],
          responseData: err.response?.data,
          responseStatus: err.response?.status,
          errorMessage: err.message,
          errorStatus: err.status
        });
        
        // Check if the error response indicates the ServiceRequest already exists
        // ApiError stores the original response data in err.raw (from apiService.handleError)
        // The backend returns the existing ServiceRequest in response.data.data when it already exists
        const errorData = err.raw || err.response?.data;
        const existingServiceRequest = errorData?.data;
        
        console.log('[CUSTOMER-SCHEDULE] Checking for existing ServiceRequest:', {
          errorDataError: errorData?.error,
          errorDataMessage: errorData?.message,
          hasExistingServiceRequest: !!existingServiceRequest,
          existingServiceRequestId: existingServiceRequest?._id || existingServiceRequest?.id,
          existingServiceRequestStatus: existingServiceRequest?.status,
          existingServiceRequestScheduledDate: existingServiceRequest?.scheduledDate,
          errorDataKeys: errorData ? Object.keys(errorData) : []
        });
        
        if ((errorData?.error === 'ServiceRequest already exists' || errorData?.message?.includes('already exists')) && existingServiceRequest && (existingServiceRequest._id || existingServiceRequest.id)) {
          console.log('[CUSTOMER-SCHEDULE] Found existing ServiceRequest, using it:', {
            id: existingServiceRequest._id || existingServiceRequest.id,
            status: existingServiceRequest.status,
            scheduledDate: existingServiceRequest.scheduledDate,
            serviceType: existingServiceRequest.serviceType
          });
          
          // The backend returned the existing ServiceRequest - use it instead
          const realJob = {
            ...job,
            _id: existingServiceRequest._id || existingServiceRequest.id,
            id: existingServiceRequest._id || existingServiceRequest.id,
            isVirtual: false,
            ...existingServiceRequest // Include all properties from the real ServiceRequest
          };
          
          // Refresh the jobs list to show the real ServiceRequest
          await fetchCustomerJobs();
          
          // Now call the edit handler with the existing ServiceRequest
          if (onEditSchedule) {
            onEditSchedule(realJob);
            onClose();
          }
        } else {
          console.error('[CUSTOMER-SCHEDULE] Could not extract existing ServiceRequest from error:', {
            errorData,
            existingServiceRequest,
            errorMessage: errorData?.message || err.message,
            rawData: err.raw
          });
          setError(errorData?.message || err.message || 'Failed to create service request. Please try again.');
        }
      } finally {
        setLoadingJobs(false);
      }
    } else {
      // Regular ServiceRequest - proceed with normal edit flow
      if (onEditSchedule) {
        onEditSchedule(job);
        onClose();
      } else {
        setError('Edit schedule handler not available');
      }
    }
  };

  const formatServiceTypeName = (job: ScheduleJob): string => {
    if (job.serviceType?.name) {
      return job.serviceType.name;
    }
    if (job.serviceType?.category) {
      const category = job.serviceType.category;
      if (category === 'mowing') return 'Lawn Mowing';
      if (category === 'fertilizing') return 'Weed Control/Fertilizing';
      if (category === 'snow_removal') return 'Snow Removal';
      return category;
    }
    return 'Service';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {defaultDate ? 'Add to Today' : 'Edit Schedules'}
      </DialogTitle>
      <DialogContent>
        {loadingCustomers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            {/* Filter - Show immediately when dialog opens, matching Add to Today */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter By</InputLabel>
                <Select
                  value={filterBy}
                  label="Filter By"
                  onChange={(e) => setFilterBy(e.target.value as any)}
                >
                  <MenuItem value="all">All Services</MenuItem>
                  <MenuItem value="lawnMowing">Lawn Mowing</MenuItem>
                  <MenuItem value="weedControl">Weed Control</MenuItem>
                  <MenuItem value="snowRemoval">Snow Removal</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="bi-weekly">Bi-weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="per-instance">Per Instance</MenuItem>
                  <MenuItem value="seasonal-contract">Seasonal Contract</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Autocomplete
              options={customers}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              value={selectedCustomer}
              onChange={(_, newValue) => setSelectedCustomer(newValue)}
              onOpen={() => setIsCustomerInputFocused(true)}
              onClose={() => setIsCustomerInputFocused(false)}
              onInputChange={(_, value) => {
                if (value) {
                  setIsCustomerInputFocused(true);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Customer"
                  placeholder={isCustomerInputFocused ? "Type Customer Name to Search" : "Select Customer"}
                  margin="normal"
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">
                      {option.firstName} {option.lastName}
                    </Typography>
                    {option.address && (
                      <Typography variant="caption" color="text.secondary">
                        {option.address.street}, {option.address.city}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />

            {selectedCustomer && (
              <>
                <Divider sx={{ my: 2 }} />
                
                {loadingJobs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : error ? (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                ) : customerJobs.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    This customer has no scheduled or accepted services. Services need to be accepted first before they can be scheduled.
                  </Alert>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Scheduled Services ({customerJobs.length})
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Click on a service to edit its schedule
                    </Typography>
                    <List>
                      {(() => {
                        // Filter jobs by service type or frequency (single filter like Add to Today)
                        let filteredJobs = [...customerJobs];
                        
                        if (filterBy !== 'all') {
                          // Filter by service type
                          if (filterBy === 'lawnMowing') {
                            filteredJobs = filteredJobs.filter((job) => {
                              const category = job.serviceType?.category;
                              return category === 'mowing';
                            });
                          } else if (filterBy === 'weedControl') {
                            filteredJobs = filteredJobs.filter((job) => {
                              const category = job.serviceType?.category;
                              return category === 'fertilizing';
                            });
                          } else if (filterBy === 'snowRemoval') {
                            filteredJobs = filteredJobs.filter((job) => {
                              const category = job.serviceType?.category;
                              return category === 'snow_removal';
                            });
                          } 
                          // Filter by frequency
                          else if (filterBy === 'weekly' || filterBy === 'bi-weekly' || filterBy === 'monthly' || filterBy === 'per-instance' || filterBy === 'seasonal-contract') {
                            filteredJobs = filteredJobs.filter((job) => {
                              const jobFrequency = job.frequency;
                              // For seasonal contracts, check if the underlying service frequency matches
                              if (jobFrequency === 'seasonal-contract' && job.acceptedBid?.servicePricing) {
                                const serviceCategory = job.serviceType?.category;
                                const matchingServicePricing = job.acceptedBid.servicePricing.find((sp: any) => {
                                  if (serviceCategory === 'mowing') return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
                                  if (serviceCategory === 'fertilizing') return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
                                  if (serviceCategory === 'snow_removal') return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
                                  return false;
                                });
                                if (matchingServicePricing?.seasonalContractDetails?.serviceFrequency) {
                                  return matchingServicePricing.seasonalContractDetails.serviceFrequency === filterBy;
                                }
                                // If seasonal contract but can't determine service frequency, show it for all frequency filters
                                return true;
                              }
                              return jobFrequency === filterBy;
                            });
                          }
                        }
                        
                        // Sort jobs by service type (mowing first, then fertilizing, then snow removal), then by frequency
                        const sortedJobs = filteredJobs.sort((a, b) => {
                          // Get service category order (mowing = 1, fertilizing = 2, snow_removal = 3)
                          const getServiceOrder = (category: string | undefined): number => {
                            if (category === 'mowing') return 1;
                            if (category === 'fertilizing') return 2;
                            if (category === 'snow_removal') return 3;
                            return 99; // Unknown services go last
                          };
                          
                          const aCategory = a.serviceType?.category;
                          const bCategory = b.serviceType?.category;
                          const aOrder = getServiceOrder(aCategory);
                          const bOrder = getServiceOrder(bCategory);
                          
                          // First sort by service type
                          if (aOrder !== bOrder) {
                            return aOrder - bOrder;
                          }
                          
                          // Then sort by frequency (weekly, bi-weekly, monthly, per-instance, seasonal-contract)
                          const getFrequencyOrder = (freq: string | undefined): number => {
                            if (!freq) return 99;
                            if (freq === 'weekly') return 1;
                            if (freq === 'bi-weekly') return 2;
                            if (freq === 'monthly') return 3;
                            if (freq === 'per-instance') return 4;
                            if (freq === 'seasonal-contract') return 5;
                            return 99;
                          };
                          
                          const aFreqOrder = getFrequencyOrder(a.frequency);
                          const bFreqOrder = getFrequencyOrder(b.frequency);
                          
                          if (aFreqOrder !== bFreqOrder) {
                            return aFreqOrder - bFreqOrder;
                          }
                          
                          // Finally, sort by service name alphabetically
                          const aName = formatServiceTypeName(a);
                          const bName = formatServiceTypeName(b);
                          return aName.localeCompare(bName);
                        });
                        
                        return sortedJobs.map((job) => {
                          const jobId = job._id || (job as any).id;
                          const serviceName = formatServiceTypeName(job);
                          const serviceCategory = job.serviceType?.category;
                        
                        // CRITICAL: Calculate next service date PER SERVICE, not per customer
                        // This matches the All Customers chart which shows each service separately
                        // For multi-service customers, each service has its own last completion date
                        let scheduledDate: string;
                        
                        // First, check if job has a scheduledDate in the future or today
                        // CRITICAL: Compare calendar dates properly to handle timezone issues
                        if (job.scheduledDate) {
                          const scheduledDateObj = new Date(job.scheduledDate);
                          const todayStr = getTodayInUserTimezone();
                          const scheduledDateStr = utcDateToUserTimezoneDateString(scheduledDateObj);
                          
                          // Compare calendar dates (strings) to avoid timezone comparison issues
                          // If scheduled date is today or in the future, show it
                          if (scheduledDateStr >= todayStr) {
                            scheduledDate = formatDateForDisplay(job.scheduledDate);
                          } else {
                            // Scheduled date is in the past, need to calculate next date
                            scheduledDate = 'Not scheduled'; // Will calculate below
                          }
                        } else {
                          scheduledDate = 'Not scheduled'; // Will calculate below
                        }
                        
                        // If not scheduled or scheduled date is in past, calculate next date from last completion
                        if (scheduledDate === 'Not scheduled') {
                          // Find last completed service for THIS specific service category
                          const completedJobsForCategory = customerJobs.filter((j: any) => {
                            const jCategory = j.serviceType?.category;
                            return j.status === 'completed' && jCategory === serviceCategory;
                          });
                          
                          if (completedJobsForCategory.length > 0) {
                            // Get most recent completed date for this service category
                            const lastCompleted = completedJobsForCategory
                              .map((j: any) => ({
                                date: j.completedDate ? new Date(j.completedDate) : null,
                                job: j
                              }))
                              .filter((item: any) => item.date !== null)
                              .sort((a: any, b: any) => b.date.getTime() - a.date.getTime())[0];
                            
                            if (lastCompleted && lastCompleted.date) {
                              // Get frequency and dayOfWeek from the job or customer service schedule
                              const frequency = job.frequency || customerServiceSchedule?.frequency;
                              const dayOfWeek = customerServiceSchedule?.dayOfWeek;
                              
                              if (frequency && (frequency === 'weekly' || frequency === 'bi-weekly' || frequency === 'monthly')) {
                                // Calculate next service date using same logic as All Customers chart
                                const calculateNextServiceDate = (completedDate: Date, freq: string, dayOfWeek?: number): Date | null => {
                                  if (!completedDate) return null;
                                  
                                  const completed = completedDate;
                                  const year = completed.getUTCFullYear();
                                  const month = completed.getUTCMonth();
                                  let day = completed.getUTCDate();
                                  let nextYear = year;
                                  let nextMonth = month;
                                  let nextDay = day;
                                  
                                  if (freq === 'weekly') {
                                    if (dayOfWeek !== undefined && dayOfWeek !== null) {
                                      const completedDayOfWeek = completed.getUTCDay();
                                      let daysToAdd = dayOfWeek - completedDayOfWeek;
                                      if (daysToAdd <= 0) {
                                        daysToAdd += 7;
                                      }
                                      nextDay += daysToAdd;
                                    } else {
                                      nextDay += 7;
                                    }
                                  } else if (freq === 'bi-weekly') {
                                    if (dayOfWeek !== undefined && dayOfWeek !== null) {
                                      const completedDayOfWeek = completed.getUTCDay();
                                      let daysToAdd = dayOfWeek - completedDayOfWeek;
                                      if (daysToAdd <= 0) {
                                        daysToAdd += 14;
                                      } else {
                                        daysToAdd += 7;
                                      }
                                      nextDay += daysToAdd;
                                    } else {
                                      nextDay += 14;
                                    }
                                  } else if (freq === 'monthly') {
                                    nextMonth += 1;
                                    if (nextMonth > 11) {
                                      nextMonth = 0;
                                      nextYear += 1;
                                    }
                                  } else {
                                    return null;
                                  }
                                  
                                  return new Date(Date.UTC(nextYear, nextMonth, nextDay, 0, 0, 0, 0));
                                };
                                
                                const calculatedNextDate = calculateNextServiceDate(lastCompleted.date, frequency, dayOfWeek);
                                if (calculatedNextDate) {
                                  scheduledDate = formatDateForDisplay(calculatedNextDate);
                                }
                              }
                            }
                          }
                        }
                        
                        return (
                          <ListItem
                            key={jobId}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              mb: 1,
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body1" fontWeight="medium">
                                    {serviceName}
                                  </Typography>
                                  {job.status && (
                                    <Chip
                                      label={job.status}
                                      size="small"
                                      color={
                                        job.status === 'scheduled' ? 'success' :
                                        job.status === 'completed' ? 'default' :
                                        job.status === 'in-progress' ? 'warning' :
                                        job.status === 'accepted' ? 'info' :
                                        'default'
                                      }
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  {scheduledDate !== 'Not scheduled' ? (
                                    <>
                                      <Typography variant="body2" color="text.secondary">
                                        Scheduled: {scheduledDate}
                                      </Typography>
                                      {job.scheduledTime && (
                                        <Typography variant="body2" color="text.secondary">
                                          Time: {job.scheduledTime}
                                        </Typography>
                                      )}
                                    </>
                                  ) : (
                                    <Typography variant="body2" color="warning.main">
                                      Not yet scheduled - Click to schedule
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => handleEditJob(job)}
                                title="Edit Schedule"
                                color="primary"
                              >
                                <Edit />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                        });
                      })()}
                    </List>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerScheduleDialog;


